// react
import { useCallback, useRef } from 'react';

// capacitor
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import {
  ActionPerformed,
  PushNotifications,
  Token,
} from '@capacitor/push-notifications';
import { Intercom as CapacitorIntercom } from '@capacitor-community/intercom';

// observability
import * as Sentry from '@sentry/react';

// store
import { useRegisterPushNotificationTokenMutation } from 'store/api/account';
import { useOpenUrl } from './useOpenUrl';

export const usePushNotification = () => {
  // hooks
  const [registerPushNotificationToken] =
    useRegisterPushNotificationTokenMutation();
  const { openUrl } = useOpenUrl();
  const actionPerformedHandle = useRef<PluginListenerHandle | null>(null);
  const registrationHandle = useRef<PluginListenerHandle | null>(null);
  const registrationErrorHandle = useRef<PluginListenerHandle | null>(null);

  // methods
  const registerForPushNotifications = useCallback(async () => {
    if (!Capacitor.isNativePlatform()) {
      console.log('Push notifications not available on web');
      return;
    }

    const platform = Capacitor.getPlatform();

    const registerToken = async (token: string, environment?: string) => {
      try {
        console.log(
          'registering push notification token:',
          token,
          'environment:',
          environment,
        );
        const response = await registerPushNotificationToken({
          token,
          platform,
          environment,
        });
        console.log('register push notification token response:', response);
      } catch (error) {
        console.error('Error registering push notification token:', error);
      }
    };

    // registration listener — iOS dispatches a custom event from AppDelegate
    // with { token, environment } where environment is detected from the
    // provisioning profile (production for TestFlight/App Store, development
    // for Xcode debug builds).
    window.addEventListener('pushRegistration', (event: any) => {
      const { token, environment } = event.detail;
      console.log(
        'push registration event:',
        token,
        'environment:',
        environment,
      );
      registerToken(token, environment);
    });

    registrationHandle.current = await PushNotifications.addListener(
      'registration',
      async (token: Token) => {
        // ios is handled by the window event listener to avoid FCM interaction
        if (platform === 'ios') {
          return;
        }

        console.log('android push registration token:', token);
        await registerToken(token.value);

        // sendPushTokenToIntercom is Android-only. On iOS the Intercom plugin
        // registers the APNs token natively via
        // capacitorDidRegisterForRemoteNotifications, posted by AppDelegate.
        try {
          await CapacitorIntercom.sendPushTokenToIntercom({
            value: token.value,
          });
        } catch (err) {
          Sentry.captureException(err, {
            tags: { component: 'intercom', operation: 'sendPushToken' },
          });
        }
      },
    );

    // error listener
    registrationErrorHandle.current = await PushNotifications.addListener(
      'registrationError',
      (error) => {
        console.error('Push notification registration error:', error);
      },
    );

    actionPerformedHandle.current = await PushNotifications.addListener(
      'pushNotificationActionPerformed',
      async (notification: ActionPerformed) => {
        console.log(notification.notification);

        const link = notification.notification.data['uri'];

        if (link) {
          console.log('link found:', link);
          openUrl(link);
        } else {
          console.log('no link found');
        }
      },
    );

    const permStatus = await PushNotifications.requestPermissions();
    console.log('push notification permission status:', permStatus);

    if (permStatus.receive !== 'granted') {
      console.error('Push notification permission denied');
      return;
    }

    await PushNotifications.register();
    console.log('push notification registered');
  }, [registerPushNotificationToken, openUrl]);

  const cleanup = useCallback(() => {
    if (Capacitor.isNativePlatform()) {
      if (actionPerformedHandle.current) {
        actionPerformedHandle.current.remove();
      }

      if (registrationHandle.current) {
        registrationHandle.current.remove();
      }

      if (registrationErrorHandle.current) {
        registrationErrorHandle.current.remove();
      }
    }
  }, []);

  return {
    registerForPushNotifications,
    cleanup,
  };
};
